import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { buildKeys, scheduleKeys, toeCheckKeys } from "../query-keys";
import type { BuildId, ToeCheckId } from "@/types/identifiers";

export { toeCheckKeys } from "../query-keys";

export function useToeChecks(buildId: BuildId) {
  return useQuery({
    queryKey: toeCheckKeys.byBuild(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/toe-checks",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to fetch toe checks");
    },
    enabled: !!buildId,
  });
}

export function useCreateToeCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await api.POST(
        "/api/builds/{build_id}/toe-checks",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to create toe check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useSignOffToeCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkId: ToeCheckId) => {
      const result = await api.POST(
        "/api/builds/{build_id}/toe-checks/{check_id}/sign-off",
        {
          params: { path: { build_id: buildId, check_id: checkId } },
        },
      );
      return throwIfError(result, "Failed to sign off toe check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUnlockToeCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkId: ToeCheckId) => {
      const result = await api.POST(
        "/api/builds/{build_id}/toe-checks/{check_id}/unlock",
        {
          params: { path: { build_id: buildId, check_id: checkId } },
        },
      );
      return throwIfError(result, "Failed to unlock toe check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useDeleteToeCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkId: ToeCheckId) => {
      const result = await api.DELETE(
        "/api/builds/{build_id}/toe-checks/{check_id}",
        {
          params: { path: { build_id: buildId, check_id: checkId } },
        },
      );
      return throwIfError(result, "Failed to delete toe check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}
